import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Users, Filter, Sparkles, TrendingUp, Bell } from "lucide-react";

const FACILITIES = [
  { slug: "all",             name: "All Facilities" },
  { slug: "badminton",       name: "Badminton" },
  { slug: "indoor-box",      name: "Indoor Box" },
  { slug: "indoor-net",      name: "Indoor Net" },
  { slug: "bowling-machine", name: "Bowling Machine" },
  { slug: "gym",             name: "Gym" },
  { slug: "swimming",        name: "Swimming" },
];

export default function AdminMembers() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ members: [], facility_counts: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = filter === "all" ? "" : `?facility=${filter}`;
    api.get(`/admin/members${q}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [filter]);

  const totalMembers = data.members.length;
  const totalActive = data.members.reduce((s, m) => s + (m.active_count || 0), 0);
  const totalRevenue = data.members.reduce(
    (s, m) => s + (m.memberships || []).reduce((a, x) => a + (x.price || 0), 0), 0);

  return (
    <div className="p-5 md:p-8" data-testid="admin-members">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// Membership Subscribers</div>
          <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1">Membership Subscribers</h1>
          <p className="text-sm text-white/60 mt-1">Members with an active gym / swim / court / turf membership plan.</p>
        </div>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/run-renewal-reminders");
              toast.success(`Sent renewal WhatsApp reminders to ${data.pinged} member(s) expiring in 7 days.`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed to send reminders");
            }
          }}
          data-testid="run-reminders-btn"
          title="Runs the 7-day renewal reminder job now (also runs daily in the background)"
          className="inline-flex items-center gap-2 border border-brand-orange/60 brand-orange px-3 py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black"
        >
          <Bell size={12} /> Send Renewal Reminders Now
        </button>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total members" value={totalMembers} icon={Users} />
        <Stat label="Active memberships" value={totalActive} icon={Sparkles} tone="orange" />
        <Stat label="Revenue" value={formatINR(totalRevenue)} icon={TrendingUp} tone="orange" />
        <Stat label="Filter" value={FACILITIES.find((f) => f.slug === filter)?.name || filter} icon={Filter} />
      </div>

      {/* per-facility counts */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="facility-counts">
        {FACILITIES.slice(1).map((f) => (
          <div key={f.slug} className="border border-white/10 bg-[#131A2B] p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{f.name}</div>
            <div className="font-display text-xl font-black brand-blue mt-1">{data.facility_counts?.[f.slug] || 0}</div>
          </div>
        ))}
      </div>

      {/* filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {FACILITIES.map((f) => (
          <button key={f.slug} onClick={() => setFilter(f.slug)}
            data-testid={`member-filter-${f.slug}`}
            className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest border ${
              filter === f.slug ? "border-brand-orange bg-brand-orange text-black" : "border-white/15 hover:border-white/40"
            }`}>{f.name}</button>
        ))}
      </div>

      {/* members list */}
      {loading ? <div className="text-white/40 py-16 text-center font-mono text-xs uppercase tracking-widest">Loading…</div> :
      data.members.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center text-white/50 text-sm">
          No members {filter !== "all" ? `for ${filter}` : "yet"}.
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto" data-testid="members-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/5">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Memberships</th>
                <th className="px-4 py-3">Facilities</th>
                <th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-t border-white/6 hover:bg-white/3" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.name}</div>
                    <div className="text-[11px] text-white/50">{m.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{m.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="brand-blue font-bold">{m.active_count}</span>
                    <span className="text-white/40"> / {m.memberships_count}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5 min-w-[220px]" data-testid={`member-memberships-${m.id}`}>
                      {(m.memberships || []).map((x) => {
                        const type = (x.plan_snapshot?.plan_type) || "hours";
                        const unit = type === "overs" ? "overs" : type === "sessions" ? "sessions" : "hrs";
                        const isUnlim = type === "unlimited";
                        const total = x.quota_total || 0;
                        const used = x.quota_used || 0;
                        const pct = isUnlim ? 100 : (total ? Math.min(100, Math.round((used / total) * 100)) : 0);
                        return (
                          <div key={x.id} className={`text-[10px] font-mono border ${x.is_active ? "border-brand-blue/30" : "border-white/10 opacity-60"} p-2`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`uppercase tracking-widest ${x.is_active ? "brand-blue" : "text-white/40"}`}>{x.facility_name || x.facility_slug}</span>
                              <span className="text-white/50">
                                {isUnlim ? "∞" : `${used}/${total} ${unit}`}
                              </span>
                            </div>
                            <div className="h-1 bg-white/8 relative overflow-hidden">
                              <div className={`h-full transition-all ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-brand-orange" : "bg-brand-blue"}`}
                                   style={{ width: `${isUnlim ? 100 : Math.max(3, pct)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-white/60 font-mono">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString("en-GB") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <div className={`border border-white/12 bg-[#131A2B] p-4 ${tone === "orange" ? "border-brand-orange/40" : ""}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
        <Icon size={11} className={tone === "orange" ? "brand-orange" : "brand-blue"} /> {label}
      </div>
      <div className={`font-display text-2xl font-black mt-1 ${tone === "orange" ? "brand-orange" : "brand-blue"}`}>{value}</div>
    </div>
  );
}
